import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs';

import { AuthService } from './auth.service';
import { IUser } from '../model/auth.model';

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  private permissionKeys = new Set<string>();
  private loaded = false;

  constructor(private authService: AuthService) {}

  hasSelfAccess(moduleName: string): boolean {
    const user = this.authService.getCurrentUser();
    if (!user || user.isAdmin) {
      return false;
    }

    const aliases = this.getModuleAliases(moduleName);
    return (user.role?.permissions || []).some(
      (permission) =>
        permission.type !== 'field' &&
        this.getModuleAliases(permission.name).some((name) => aliases.includes(name)) &&
        Number(permission.RolePermission?.accessLevel ?? permission.accessLevel ?? 0) === 4,
    );
  }

  isAssignedEmployee(employeeId?: string | null): boolean {
    const user = this.authService.getCurrentUser();
    if (!user) return false;
    if (user.isAdmin) return true;
    return !!user.id && !!employeeId && user.id === employeeId;
  }

  hasPermission(key: string): boolean {
    const user = this.authService.getCurrentUser();
    if (!user) {
      return false;
    }

    if (user.isAdmin) {
      return true;
    }

    if (!this.loaded) {
      this.buildPermissionCache(user);
    }

    return this.permissionKeys.has(key);
  }

  hasColumnPermission(
    module: string,
    column: string,
    access: 'view' | 'edit' = 'view',
  ): boolean {
    return this.getModuleAliases(module).some((moduleAlias) =>
      this.getColumnAliases(column).some((columnAlias) =>
        this.hasPermission(`${moduleAlias}.columns.${columnAlias}.${access}`),
      ),
    );
  }

  canViewColumn(module: string, column: string): boolean {
    return this.hasColumnPermission(module, column, 'view');
  }

  canEditColumn(module: string, column: string): boolean {
    return this.hasColumnPermission(module, column, 'edit');
  }

  hasAnyPermission(keys: string[]): boolean {
    return keys.some((key) => this.hasPermission(key));
  }

  loadPermissions(): Observable<string[]> {
    const user = this.authService.getCurrentUser();
    if (user && user.role) {
      this.buildPermissionCache(user);
      return of(this.getPermissionKeys());
    }

    return this.authService.loadProfileIfNeeded().pipe(
      map((profileUser) => {
        this.buildPermissionCache(profileUser);
        return this.getPermissionKeys();
      }),
    );
  }

  private getPermissionKeys(): string[] {
    return Array.from(this.permissionKeys);
  }

  private buildPermissionCache(user: IUser | null): void {
    this.permissionKeys.clear();
    this.loaded = false;

    if (!user) {
      return;
    }

    if (user.isAdmin) {
      this.loaded = true;
      return;
    }

    const permissions = user.role?.permissions || [];
    permissions.forEach((permission) => this.addPermissionKeys(permission));
    this.loaded = true;
  }

  private addPermissionKeys(permission: any): void {
    const accessLevel = Number(
      permission.RolePermission?.accessLevel ?? permission.accessLevel ?? 0,
    );

    if (!permission.name || accessLevel <= 0) {
      return;
    }

    const fieldPermission = this.parseFieldPermissionName(permission.name);

    if (
      permission.type === 'field' ||
      (fieldPermission && !this.isModuleAction(fieldPermission.column))
    ) {
      this.addFieldPermissionKeys(permission.name, accessLevel);
      return;
    }

    this.addModulePermissionKeys(permission.name, accessLevel);
  }

  private addModulePermissionKeys(moduleName: string, accessLevel: number): void {
    const moduleAliases = this.getModuleAliases(moduleName);

    moduleAliases.forEach((module) => {
      this.permissionKeys.add(`${module}.view`);

      if (accessLevel >= 2) {
        [
          'create',
          'edit',
          'update',
          'send',
          'upload',
          'security_in',
          'store_in',
          'download',
          'cancel',
        ].forEach((action) => this.permissionKeys.add(`${module}.${action}`));
      }

      if (accessLevel >= 3) {
        this.permissionKeys.add(`${module}.delete`);
      }

      if (accessLevel === 4) {
        this.permissionKeys.add(`${module}.self`);
      }
    });
  }

  private addFieldPermissionKeys(fieldName: string, accessLevel: number): void {
    const fieldPermission = this.parseFieldPermissionName(fieldName);
    if (!fieldPermission) {
      return;
    }

    const { moduleName, column } = fieldPermission;

    this.getModuleAliases(moduleName).forEach((module) => {
      this.getColumnAliases(column).forEach((columnAlias) => {
        this.permissionKeys.add(`${module}.columns.${columnAlias}.view`);

        if (accessLevel >= 2) {
          this.permissionKeys.add(`${module}.columns.${columnAlias}.edit`);
        }
      });
    });
  }

  private parseFieldPermissionName(
    fieldName: string,
  ): { moduleName: string; column: string } | null {
    const parts = fieldName.split('.').filter(Boolean);
    const moduleName = parts[0];

    if (!moduleName) {
      return null;
    }

    if (parts.length >= 4 && parts[1] === 'columns') {
      return { moduleName, column: parts[2] };
    }

    if (parts.length >= 2) {
      return { moduleName, column: parts[1] };
    }

    return null;
  }

  private getColumnAliases(column: string): string[] {
    const aliases = new Set<string>([column]);
    const snakeCase = column.replace(
      /[A-Z]/g,
      (letter) => `_${letter.toLowerCase()}`,
    );

    aliases.add(snakeCase);
    aliases.add(
      column.replace(/_([a-z])/g, (_, letter: string) =>
        letter.toUpperCase(),
      ),
    );

    return Array.from(aliases);
  }

  private isModuleAction(value: string): boolean {
    return [
      'view',
      'create',
      'edit',
      'update',
      'send',
      'upload',
      'security_in',
      'store_in',
      'download',
      'cancel',
      'delete',
      'self',
    ].includes(value);
  }

  private getModuleAliases(moduleName: string): string[] {
    const aliases = new Set<string>([moduleName]);
    aliases.add(moduleName.replace(/_/g, '-'));
    return Array.from(aliases);
  }
}
